import { useState, useRef, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { MessageCircle, X, Send, Gem, ArrowUpRight } from "lucide-react";
import { api, API } from "@/lib/apiClient";
import { log } from "@/lib/log";

const DIAGNOSTIC_PATH = "/diagnostic-inaugural";

const GREETING =
  "Bienvenue. Je suis L'Émissaire de l'Académie AETHERIS. Quelle est la problématique principale que vous traversez en ce moment ?";

const CTA_TRIGGER = /diagnostic de souverainet[ée]/i;

// Identifiant de message stable pour les clés React (jamais l'index du tableau).
let msgSeq = 0;
const mkMsg = (from, text) => ({ id: `m${Date.now().toString(36)}-${++msgSeq}`, from, text });

// Seul un identifiant de session anonyme est persisté — jamais le contenu des conversations.
function genSessionId() {
  try {
    let s = localStorage.getItem("emissaire_session");
    if (!s) {
      s = "s-" + Date.now().toString(36) + "-" + Math.random().toString(36).slice(2, 8);
      localStorage.setItem("emissaire_session", s);
    }
    return s;
  } catch (e) {
    log.warn("[emissaire] localStorage indisponible, session éphémère :", e);
    return "s-" + Date.now().toString(36);
  }
}

export const Emissaire = () => {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState(() => [mkMsg("bot", GREETING)]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [ctaShown, setCtaShown] = useState(false);
  const scrollRef = useRef(null);

  const sessionIdRef = useRef(null);
  if (!sessionIdRef.current) sessionIdRef.current = genSessionId();

  const startedAtRef = useRef(new Date().toISOString());
  const startTsRef = useRef(Date.now());
  const outcomeRef = useRef("non_converti");
  const reportedRef = useRef(false);
  const messagesRef = useRef(messages);

  useEffect(() => { messagesRef.current = messages; }, [messages]);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, open]);

  const sendReport = useCallback((viaBeacon = false) => {
    const userMsgs = messagesRef.current.filter((m) => m.from === "user");
    if (reportedRef.current || userMsgs.length === 0) return;
    reportedRef.current = true;
    const endedAt = new Date().toISOString();
    const durationSeconds = Math.max(1, Math.round((Date.now() - startTsRef.current) / 1000));
    const problematique = userMsgs[0]?.text?.slice(0, 280) || null;
    const payload = {
      started_at: startedAtRef.current, ended_at: endedAt,
      duration_seconds: durationSeconds, outcome: outcomeRef.current, problematique,
      messages: messagesRef.current.map((m) => ({ role: m.from === "user" ? "user" : "bot", content: m.text })),
    };
    try {
      const url = `${API}/emissaire-report`;
      if (viaBeacon && navigator.sendBeacon) {
        navigator.sendBeacon(url, new Blob([JSON.stringify(payload)], { type: "application/json" }));
      } else {
        api.post("/emissaire-report", payload).catch((e) => log.warn("[emissaire] rapport non envoyé :", e));
      }
    } catch (e) { log.warn("[emissaire] rapport non envoyé :", e); }
  }, []);

  useEffect(() => {
    const handler = () => sendReport(true);
    window.addEventListener("pagehide", handler);
    window.addEventListener("beforeunload", handler);
    return () => {
      window.removeEventListener("pagehide", handler);
      window.removeEventListener("beforeunload", handler);
      sendReport(false);
    };
  }, [sendReport]);

  const handleCtaClick = () => {
    outcomeRef.current = "converti";
    sendReport(false);
  };

  const send = async (e) => {
    e?.preventDefault?.();
    const text = input.trim();
    if (!text || sending) return;
    setInput("");
    setSending(true);
    // Ajoute le message user + un placeholder bot
    setMessages((m) => [...m, mkMsg("user", text), mkMsg("bot", "")]);

    try {
      const res = await fetch(`${API}/ai/emissaire/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: sessionIdRef.current, message: text }),
      });

      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      let botAccum = "";

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const parts = buffer.split("\n\n");
        buffer = parts.pop();
        for (const chunk of parts) {
          if (!chunk.startsWith("data: ")) continue;
          let evt;
          try {
            evt = JSON.parse(chunk.slice(6));
          } catch (e) {
            log.warn("[emissaire] chunk SSE malformé ignoré :", e);
            continue;
          }
          if (evt.error) throw new Error(evt.error);
          if (evt.delta) {
            botAccum += evt.delta;
            setMessages((m) => {
              const copy = [...m];
              copy[copy.length - 1] = { ...copy[copy.length - 1], text: botAccum };
              return copy;
            });
          }
        }
      }

      // Détecte si Claude a proposé le Diagnostic → afficher le bouton CTA
      if (CTA_TRIGGER.test(botAccum) && !ctaShown) {
        setCtaShown(true);
      }
    } catch (err) {
      log.error("[emissaire] échec de la réponse :", err);
      setMessages((m) => {
        const copy = [...m];
        copy[copy.length - 1] = { ...copy[copy.length - 1], text: "Je rencontre un souci technique. Merci de réessayer dans un instant." };
        return copy;
      });
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      <button
        data-testid="emissaire-toggle"
        onClick={() => setOpen((v) => !v)}
        className="fixed bottom-6 right-6 z-[70] w-14 h-14 rounded-full bg-gold text-brand-ink flex items-center justify-center shadow-[0_10px_30px_rgba(201,168,76,0.4)] hover:scale-105 transition-transform"
        aria-label="Émissaire"
      >
        {open ? <X strokeWidth={2} /> : <MessageCircle strokeWidth={2} />}
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="emissaire-panel"
            initial={{ opacity: 0, y: 30, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 30, scale: 0.96 }}
            transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
            className="fixed bottom-24 right-6 z-[70] w-[92vw] max-w-sm h-[68vh] max-h-[560px] flex flex-col bg-brand-ink border border-gold/30 shadow-[0_20px_60px_rgba(0,0,0,0.6)]"
          >
            <div className="flex items-center gap-3 p-5 border-b border-white/10">
              <div className="w-9 h-9 rounded-full bg-gold/15 flex items-center justify-center">
                <Gem size={16} className="text-gold" />
              </div>
              <div>
                <p className="font-serif text-lg text-white leading-none">L'Émissaire</p>
                <p className="text-[11px] uppercase tracking-luxe text-gold mt-1">
                  Académie AETHERIS
                </p>
              </div>
            </div>

            <div
              ref={scrollRef}
              className="flex-1 overflow-y-auto p-5 space-y-4"
              data-lenis-prevent
              data-testid="emissaire-messages"
            >
              {messages.map((m) => (
                <div key={m.id} className={`flex ${m.from === "user" ? "justify-end" : "justify-start"}`}>
                  <div
                    className={`max-w-[85%] px-4 py-3 text-sm leading-relaxed ${
                      m.from === "user"
                        ? "bg-gold text-brand-ink"
                        : "bg-brand-surface text-white/80 border border-white/10"
                    }`}
                  >
                    {m.text}
                  </div>
                </div>
              ))}
            </div>

            {ctaShown && (
              <Link
                to={DIAGNOSTIC_PATH}
                onClick={handleCtaClick}
                data-testid="emissaire-cta"
                className="mx-4 mb-2 bg-gold text-brand-ink px-4 py-3 text-xs uppercase tracking-[0.15em] font-semibold inline-flex items-center justify-center gap-2 hover:bg-brand-gold-hover transition-colors"
              >
                Demandez votre Diagnostic Inaugural — Gratuit <ArrowUpRight size={14} />
              </Link>
            )}

            <form onSubmit={send} className="p-4 border-t border-white/10 flex gap-2">
              <input
                data-testid="emissaire-input"
                type="text"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="Écrivez votre réponse…"
                className="flex-1 bg-transparent border border-white/15 focus:border-gold rounded-none text-white px-3 py-2.5 text-sm outline-none transition-colors placeholder-white/30"
              />
              <button
                type="submit"
                data-testid="emissaire-send"
                aria-label="Envoyer"
                disabled={!input.trim() || sending}
                className="bg-gold text-brand-ink px-4 py-2.5 hover:bg-brand-gold-hover transition-colors disabled:opacity-40"
              >
                <Send size={16} />
              </button>
            </form>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
